import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { useSnackbar } from 'notistack';
import type { ChangeEvent, FormEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axios from 'axios';

import Header from 'components/Header';

import Route from 'routes/Route';

import { useUser } from 'hooks/useUser';
import { useToken } from 'hooks/useToken';

import styles from './styles';

interface LoginState {
  username: string;
  password: string;
}

const INITIAL_STATE: LoginState = {
  username: '',
  password: '',
};

const Home = (): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setState] = useState<LoginState>(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateUser } = useUser();
  const { token, updateToken } = useToken();
  const navigate = useNavigate();

  const handleLogin = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsSubmitting(true);
      try {
        const response = await axios.post('auth/', data);
        updateUser(response);
        updateToken(response.data.token);
        navigate(Route.DASHBOARD);
      } catch {
        enqueueSnackbar('Erro ao realizar o login', { variant: 'error' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, enqueueSnackbar, navigate, updateToken, updateUser],
  );

  const onChangeTextField = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void =>
      setState(prevState => ({
        ...prevState,
        [event.target.name]: event.target.value,
      })),
    [],
  );

  useEffect(() => {
    if (token) {
      navigate(Route.DASHBOARD);
    }
  }, [navigate, token]);

  return (
    <Container sx={styles.verticalContainer}>
      <Container maxWidth="sm">
        <Stack sx={styles.innerStack} spacing={2}>
          <Header title="Entrar" />
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises -- async login with snackbar on error */}
          <form noValidate onSubmit={handleLogin} aria-busy={isSubmitting}>
            <Stack spacing={2}>
              <TextField
                type="email"
                name="username"
                label="Email"
                value={data.username}
                onChange={onChangeTextField}
                margin="normal"
                variant="outlined"
                autoComplete="username"
                disabled={isSubmitting}
              />
              <TextField
                type="password"
                name="password"
                label="Senha"
                value={data.password}
                onChange={onChangeTextField}
                margin="normal"
                variant="outlined"
                autoComplete="current-password"
                disabled={isSubmitting}
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={isSubmitting}
                aria-label={isSubmitting ? 'Entrando, aguarde' : 'Entrar'}
              >
                {isSubmitting ? (
                  <Box sx={styles.submitProgress}>
                    <CircularProgress size={22} color="inherit" aria-hidden />
                    <span>Entrando…</span>
                  </Box>
                ) : (
                  'Login'
                )}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Container>
    </Container>
  );
};

export default Home;
